import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import {
  LookupDef,
  LookupListResponse,
  LookupRow,
} from "src/dm/interfaces/AdminLookup";

const baseUrl = "/dm/V1/admin/lookups";

interface CatalogResponse {
  items: LookupDef[];
  total: number;
}

export const useLookupCatalog = () =>
  useQuery({
    queryKey: ["admin-lookups"],
    queryFn: async (): Promise<LookupDef[]> => {
      const response = await apiClient.get<CatalogResponse>(baseUrl);
      return response.data.items;
    },
  });

export const useLookupRows = (name: string | undefined) =>
  useQuery({
    queryKey: ["admin-lookup-rows", name],
    enabled: !!name,
    queryFn: async (): Promise<LookupListResponse> => {
      const response = await apiClient.get<LookupListResponse>(
        `${baseUrl}/${name}`
      );
      return response.data;
    },
  });

export const useAddLookup = (name: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: LookupRow): Promise<LookupRow> => {
      const response = await apiClient.post<LookupRow>(
        `${baseUrl}/${name}`,
        row
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-lookup-rows", name] });
    },
  });
};

export const useUpdateLookup = (name: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: LookupRow): Promise<LookupRow> => {
      const response = await apiClient.put<LookupRow>(
        `${baseUrl}/${name}/${encodeURIComponent(row.id)}`,
        row
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-lookup-rows", name] });
    },
  });
};

export const useDeleteLookup = (name: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`${baseUrl}/${name}/${encodeURIComponent(id)}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-lookup-rows", name] });
    },
  });
};
